import {
    ApproveEntityGet,
    ApproveOpenItem,
    Decimal,
    SubscribeState,
    Ulid,
} from "@kase/my-marathon-communication";
import { computed, signal } from "@preact/signals";
import { upsert } from "~/utils";
import { ApiSliceParams } from "../interfaces";
import { createListController, deferDocument } from "../utils";
import {
    Approval,
    ApprovalAccountingCost,
    ApprovalAccountingPurchase,
    ApprovalItem,
    ApprovalItemAccounting,
    ApproveParams,
    CreateProjectPurchaseParams,
} from "./interfaces";

const getId = (item: ApproveEntityGet) =>
    `${item.id.kind}-${item.id.company}-${item.id.id.toString()}`;

const getOpenId = (item: Readonly<ApproveOpenItem>) => {
    if (item.invoice.entity.$case === "approval") {
        if (item.invoice.entity.approval.id) {
            return Ulid.fromLoHi(item.invoice.entity.approval.id).toString();
        }
    } else if (item.invoice.entity.accounting.id) {
        return Ulid.fromLoHi(item.invoice.entity.accounting.id).toString();
    }

    return "$";
};

export const makeApproval = ({ connection }: ApiSliceParams) => {
    const open = async (approval: Approval) => {
        approval.loading.value = true;

        // @TODO: remove this timeout
        window.setTimeout(() => {
            if (!approval.loaded.peek()) {
                approval.loading.value = false;
                approval.loaded.value = true;
            }
        }, 5000);

        for await (const event of connection.value.approval.open(
            approval._id,
        )) {
            if (event.kind === SubscribeState.Remove) {
                // approval.items.value = approval.items.peek()
                // .filter(
                //     (r) => !event.values.find((v) => getOpenId(v) === r.id),
                // );
            } else {
                approval.items.value = upsert(
                    approval.items.peek().slice(),
                    event.values.map((i) => ({ ...i, id: getOpenId(i) })),
                    "id",
                );

                if (
                    event.kind === SubscribeState.LoadedCache ||
                    event.kind === SubscribeState.Loaded
                ) {
                    approval.loaded.value = true;
                    approval.loading.value = false;
                }
            }
        }
    };

    const info = async (approval: Approval) => {
        for await (const item of connection.value.approval.info({
            id: [approval._id],
        })) {
            approval.info.value = {
                ...item,
                id: item.id.toString(),
                _id: item.id,
            };
        }
    };

    const getDocument = (approval: ApproveEntityGet) =>
        deferDocument(
            connection.value.approval.getDocument({
                approve_entity_id: approval.id.id,
                company: approval.id.company,
            }),
        );

    const view = async (approval: ApproveEntityGet) => {
        const items = signal<ApprovalItem[]>([]);
        const finalApproval: Approval = {
            ...approval,
            id: getId(approval),
            _id: approval.id,
            company: approval.id.company,
            loading: signal(false),
            loaded: signal(false),
            items,
            document: getDocument(approval),
            approvals: computed(() =>
                items.value.flatMap((i) =>
                    i.invoice.entity.$case === "approval"
                        ? i.invoice.entity.approval
                        : [],
                ),
            ),
            purchases: computed(() =>
                items.value.filter(
                    (item): item is ApprovalAccountingPurchase =>
                        item.$case === "invoice" &&
                        item.invoice.entity.$case === "accounting" &&
                        item.invoice.entity.accounting.kind?.$case ===
                            "purchase",
                ),
            ),
            costs: computed(() =>
                items.value.filter(
                    (item): item is ApprovalAccountingCost =>
                        item.$case === "invoice" &&
                        item.invoice.entity.$case === "accounting" &&
                        item.invoice.entity.accounting.kind?.$case === "cost",
                ),
            ),
            info: signal(null),
        };

        open(finalApproval);
        info(finalApproval);

        return finalApproval;
    };

    const {
        allLoading: loading,
        allLoaded: loaded,
        items: approvals,
        listen,
    } = createListController<ApproveEntityGet, Approval>({
        connection,
        list: (con) => con.approval.items(),
        getId,
        view,
    });

    const createProjectPurchase = ({
        id,
        purchasePrice,
        comment,
        vatClass,
        markUp,
        purchaseCode,
        purchaseOrder,
        project,
    }: CreateProjectPurchaseParams) => {
        return connection.value.approval.accounting.create({
            approve_entity_id: id,
            purchase_price: Decimal.from(purchasePrice),
            comment,
            vat_class: vatClass,
            kind: {
                $case: "purchase",
                purchase: {
                    purchase_order: purchaseOrder,
                    project,
                    purchase_code: purchaseCode,
                    mark_up: markUp ? Decimal.from(markUp) : undefined,
                },
            },
        });
    };

    const approve = ({ id, amount, kind, comment }: ApproveParams) => {
        return connection.value.approval.approve({
            approve_entity_id: id,
            amount: Decimal.from(amount),
            kind,
            comment,
        });
    };

    return {
        approvals,
        loading,
        loaded,
        listen,
        approve,
        createProjectPurchase,
    };
};
